import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const Orientations = () => {
  const [orientations, setOrientations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/admin/api/orientations.php');
        const data = await res.json();
        if (Array.isArray(data)) setOrientations(data);
        else if (data.data) setOrientations(data.data);
      } catch { /* silent */ } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
  const fmtTime = (s) => s ? new Date(`2000-01-01T${s}`).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '';

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="page-enter">
      <Helmet>
        <title>Orientations — PAK School &amp; College System | Welcome Programs</title>
        <meta name="description" content="Join PAK School & College System's orientation programs. Comprehensive sessions for new students and parents covering school culture, policies, and expectations." />
        <link rel="canonical" href="https://pakschoolandcollege.com/orientations" />
      </Helmet>

      <div className="page-hero">
        <div className="page-hero-content">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm mb-5">
            🤝 Welcome Programs
          </div>
          <h1>Orientation <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">Sessions</span></h1>
          <p>Comprehensive orientation programs designed to help new students and parents understand our school community</p>
        </div>
      </div>

      <div className="py-14 bg-white min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {loading && (
            <div className="flex flex-col items-center py-24 gap-4">
              <div className="spinner" />
              <p className="text-gray-400 text-sm font-inter">Loading orientations...</p>
            </div>
          )}

          {!loading && orientations.length === 0 && (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-5">
                <i className="fas fa-calendar-check text-3xl text-gray-300" />
              </div>
              <h3 className="font-poppins font-700 text-gray-700 text-xl mb-2">No Orientations Scheduled</h3>
              <p className="text-gray-400 font-inter text-sm">Check back later for upcoming orientation sessions.</p>
              <div className="mt-6">
                <a href="tel:03430001144" className="btn-primary inline-flex">
                  <i className="fas fa-phone" /> Call for Info
                </a>
              </div>
            </div>
          )}

          {!loading && orientations.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {orientations.map((orientation) => (
                <div key={orientation.id}
                  className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 overflow-hidden">
                  {orientation.image_path && (
                    <div className="h-44 overflow-hidden">
                      <img src={`admin/${orientation.image_path}`} alt={orientation.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`text-xs font-poppins font-700 px-2.5 py-1 rounded-full border ${orientation.status === 'active' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-primary/10 text-primary border-primary/20'}`}>
                        {orientation.status === 'active' ? 'Active' : 'Upcoming'}
                      </span>
                      {orientation.duration && (
                        <span className="text-xs text-gray-400 font-inter">{orientation.duration} min</span>
                      )}
                    </div>
                    <h3 className="font-poppins font-700 text-navy text-base mb-3 group-hover:text-primary transition-colors">{orientation.name}</h3>
                    <div className="space-y-1.5 mb-3">
                      {orientation.date && <div className="flex items-center gap-2 text-xs text-gray-400 font-inter"><i className="fas fa-calendar-alt text-primary w-4 text-center" />{fmtDate(orientation.date)}</div>}
                      {orientation.time && <div className="flex items-center gap-2 text-xs text-gray-400 font-inter"><i className="fas fa-clock text-green-500 w-4 text-center" />{fmtTime(orientation.time)}</div>}
                      {orientation.location && <div className="flex items-center gap-2 text-xs text-gray-400 font-inter"><i className="fas fa-map-marker-alt text-accent w-4 text-center" />{orientation.location}</div>}
                      {orientation.audience && <div className="flex items-center gap-2 text-xs text-gray-400 font-inter"><i className="fas fa-users text-purple-500 w-4 text-center" />{orientation.audience}</div>}
                    </div>
                    {orientation.description && (
                      <p className="text-gray-400 text-xs font-inter leading-relaxed line-clamp-2 mb-4">{orientation.description}</p>
                    )}
                    <div className="flex items-center justify-between">
                      {orientation.instructor && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-400 font-inter">
                          <i className="fas fa-user-tie text-gray-300" /> {orientation.instructor}
                        </div>
                      )}
                      <button onClick={() => setSelected(orientation)}
                        className="ml-auto flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-xs font-poppins font-600 rounded-xl hover:bg-primary-dark transition-colors">
                        Details →
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {selected && (
        <div className="lightbox-overlay" onClick={() => setSelected(null)} role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 flex items-center justify-between p-5 rounded-t-2xl z-10">
              <h2 className="font-poppins font-700 text-navy text-lg pr-4">{selected.name}</h2>
              <button onClick={() => setSelected(null)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 flex-shrink-0">
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            <div className="p-6">
              {selected.image_path && (
                <img src={`admin/${selected.image_path}`} alt={selected.name}
                  className="w-full h-56 object-cover rounded-xl mb-5" />
              )}
              <div className="grid sm:grid-cols-2 gap-4 mb-5">
                {[
                  { icon: 'fa-calendar-alt', label: 'Date & Time', value: `${fmtDate(selected.date)} at ${fmtTime(selected.time)}`, color: 'bg-primary/10', iconColor: 'text-primary' },
                  selected.location && { icon: 'fa-map-marker-alt', label: 'Location', value: selected.location, color: 'bg-accent/10', iconColor: 'text-accent' },
                  selected.audience && { icon: 'fa-users', label: 'Audience', value: selected.audience, color: 'bg-green-100', iconColor: 'text-green-600' },
                  selected.duration && { icon: 'fa-clock', label: 'Duration', value: `${selected.duration} minutes`, color: 'bg-purple-100', iconColor: 'text-purple-600' },
                ].filter(Boolean).map((item, i) => (
                  <div key={i} className={`${item.color} rounded-xl p-4`}>
                    <p className="font-poppins font-600 text-gray-700 text-sm mb-0.5 flex items-center gap-2">
                      <i className={`fas ${item.icon} ${item.iconColor}`} />{item.label}
                    </p>
                    <p className="text-gray-600 font-inter text-sm">{item.value}</p>
                  </div>
                ))}
              </div>
              {selected.description && (
                <div className="mb-4">
                  <h4 className="font-poppins font-700 text-navy text-sm mb-2">Description</h4>
                  <p className="text-gray-500 font-inter text-sm leading-relaxed">{selected.description}</p>
                </div>
              )}
              {selected.instructor && (
                <div className="bg-primary/5 border border-primary/10 rounded-xl p-4">
                  <p className="font-poppins font-600 text-navy text-sm mb-0.5">Instructor</p>
                  <p className="text-gray-500 font-inter text-sm">{selected.instructor}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Orientations;
